import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, ChevronDown, ChevronRight, Info, Pencil, X } from 'lucide-react';
import { useUserProfile } from '../../context/UserProfileContext';
import { useOrgSettings } from '../../hooks/useOrgSettings';
import { useOrgResourceRates } from '../../hooks/useOrgResourceRates';
import { useResidentialFlatRates } from '../../hooks/useResidentialFlatRates';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import {
  OrgCurrency,
  CommercialBallparkMethod,
  ResourceRate,
  ResourceSlug,
  ResidentialFlatRates,
  ResidentialFlatRateRoom,
} from '../../types/admin';

// ── Helpers ─────────────────────────────────────────────────────────────────

const CURRENCY_SYMBOL: Record<OrgCurrency, string> = {
  USD: '$',
  EUR: '€',
  GBP: '£',
};

function formatRate(rate: number, currency: OrgCurrency): string {
  return `${CURRENCY_SYMBOL[currency]}${rate}/hr`;
}

function formatFlatRate(rate: number, currency: OrgCurrency): string {
  return `${CURRENCY_SYMBOL[currency]}${rate}`;
}

// ── Crew cost calculation for hourly-rate room estimates ─────────────────────

interface CrewCostBreakdown {
  supervisorQty: number;
  supervisorRate: number;
  moverQty: number;
  moverRate: number;
  truckQty: number;
  truckRate: number;
  truckDriverQty: number;
  truckDriverRate: number;
  totalHourlyRate: number;
  hours: number;
  totalCost: number;
  useBlended: boolean;
}

function computeCrewCost(
  crewSize: number,
  trucks: number,
  hours: number,
  rates: ResourceRate[],
  useBlended: boolean,
): CrewCostBreakdown {
  const rateMap = new Map(rates.map(r => [r.slug, r.hourlyRate]));
  const supervisorQty = 1;
  const supervisorRate = rateMap.get('supervisor') ?? 0;
  const moverRate = rateMap.get('mover') ?? 0;
  const truckRate = rateMap.get('truck') ?? 0;
  const truckDriverRate = rateMap.get('truck_driver') ?? 0;

  let totalHourlyRate: number;
  let moverQty: number;
  let truckDriverQty: number;

  if (useBlended) {
    truckDriverQty = trucks;
    moverQty = Math.max(0, crewSize - supervisorQty - truckDriverQty);
    totalHourlyRate =
      supervisorQty * supervisorRate +
      truckDriverQty * truckDriverRate +
      moverQty * moverRate;
  } else {
    truckDriverQty = 0;
    moverQty = Math.max(0, crewSize - supervisorQty);
    totalHourlyRate =
      supervisorQty * supervisorRate +
      moverQty * moverRate +
      trucks * truckRate;
  }

  return {
    supervisorQty,
    supervisorRate,
    moverQty,
    moverRate,
    truckQty: trucks,
    truckRate,
    truckDriverQty,
    truckDriverRate,
    totalHourlyRate,
    hours,
    totalCost: totalHourlyRate * hours,
    useBlended,
  };
}

// ── Tooltip component ────────────────────────────────────────────────────────

function Tooltip({ children, content }: { children: React.ReactNode; content: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div ref={ref} className="relative inline-flex">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="inline-flex items-center"
      >
        {children}
      </button>
      {open && (
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-50
                        w-[260px] sm:w-[300px] p-3 rounded-xl border border-stone bg-white shadow-lg
                        text-[12px] text-midnight leading-relaxed">
          {content}
          <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-[1px]
                          w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px]
                          border-l-transparent border-r-transparent border-t-stone" />
        </div>
      )}
    </div>
  );
}

// ── Collapsible section ──────────────────────────────────────────────────────

interface CollapsibleCardProps {
  title: string;
  defaultExpanded?: boolean;
  expandable?: boolean;
  disabled?: boolean;
  headerRight?: React.ReactNode;
  children: React.ReactNode;
}

function CollapsibleCard({
  title,
  defaultExpanded = true,
  expandable = true,
  disabled = false,
  headerRight,
  children,
}: CollapsibleCardProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  return (
    <div
      className={`bg-white border border-stone rounded-xl shadow-brand-sm overflow-hidden
                  ${disabled ? 'opacity-60' : ''}`}
    >
      <div
        role="button"
        tabIndex={expandable ? 0 : -1}
        onClick={() => expandable && setExpanded(!expanded)}
        onKeyDown={e => { if (expandable && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); setExpanded(!expanded); } }}
        className={`w-full flex items-center gap-3 p-4 md:p-5 text-left
                   ${expandable ? 'hover:bg-mist cursor-pointer' : 'cursor-default'}
                   transition-colors duration-150`}
      >
        {expandable && (
          expanded
            ? <ChevronDown size={18} className="shrink-0 text-slate" />
            : <ChevronRight size={18} className="shrink-0 text-slate" />
        )}
        {!expandable && <ChevronRight size={18} className="shrink-0 text-stone" />}
        <span className="flex-1 font-semibold text-small text-midnight">{title}</span>
        {headerRight && (
          <div onClick={e => e.stopPropagation()} className="shrink-0">
            {headerRight}
          </div>
        )}
      </div>
      {expanded && expandable && (
        <div className="px-4 pb-4 md:px-5 md:pb-5 border-t border-stone">
          {children}
        </div>
      )}
    </div>
  );
}

// ── Toggle switch ────────────────────────────────────────────────────────────

interface ToggleSwitchProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  label: string;
}

function ToggleSwitch({ checked, onChange, disabled, label }: ToggleSwitchProps) {
  return (
    <label className="inline-flex items-center gap-2 cursor-pointer select-none">
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-6 w-11 shrink-0 rounded-full border-2 border-transparent
                   transition-colors duration-200 ease-in-out
                   focus:outline-none focus:ring-2 focus:ring-plum/20
                   ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                   ${checked ? 'bg-plum' : 'bg-stone'}`}
      >
        <span
          className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow-sm
                     ring-0 transition-transform duration-200 ease-in-out
                     ${checked ? 'translate-x-5' : 'translate-x-0'}`}
        />
      </button>
      <span className="text-[13px] text-midnight">{label}</span>
    </label>
  );
}

// ── Rate line item (read-only / editable) ────────────────────────────────────

interface RateLineItemProps {
  name: string;
  rate: number;
  currency: OrgCurrency;
  formatFn?: (rate: number, currency: OrgCurrency) => string;
  editing: boolean;
  onEdit: () => void;
  onSave: (newRate: number) => Promise<void>;
  onCancel: () => void;
  isEditing: boolean;
  disabled?: boolean;
  /** Extra content to show below the name (e.g. checkbox) */
  extra?: React.ReactNode;
  /** Suppress the bottom border (when parent handles borders). */
  noBorder?: boolean;
}

function RateLineItem({
  name,
  rate,
  currency,
  formatFn = formatRate,
  editing,
  onEdit,
  onSave,
  onCancel,
  isEditing,
  disabled,
  extra,
  noBorder,
}: RateLineItemProps) {
  const [value, setValue] = useState(String(rate));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setValue(String(rate));
  }, [rate]);

  function handleOpen() {
    setValue(String(rate));
    setError('');
    onEdit();
  }

  async function handleSave() {
    const num = parseFloat(value);
    if (isNaN(num) || num < 0) {
      setError('Enter a valid rate (0 or greater).');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await onSave(num);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save.');
    }
    setSaving(false);
  }

  return (
    <div className={`py-3 ${noBorder ? '' : 'border-b border-stone last:border-b-0'}`}>
      <div className="flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <p className={`text-small ${disabled ? 'text-slate' : 'text-midnight'}`}>{name}</p>
          {extra}
        </div>
        {!isEditing && (
          <>
            <span className={`font-mono text-small font-bold tabular-nums ${disabled ? 'text-slate' : 'text-midnight'}`}>
              {formatFn(rate, currency)}
            </span>
            {editing && !disabled && (
              <button
                type="button"
                onClick={handleOpen}
                className="shrink-0 h-8 w-8 flex items-center justify-center rounded-lg border border-stone
                           text-slate hover:border-plum hover:text-plum active:bg-mist
                           transition-colors duration-150"
                aria-label={`Edit ${name} rate`}
              >
                <Pencil size={14} />
              </button>
            )}
          </>
        )}
        {isEditing && (
          <button
            type="button"
            onClick={() => { setError(''); onCancel(); }}
            className="shrink-0 h-8 w-8 flex items-center justify-center rounded-lg border border-stone
                       text-slate hover:text-midnight active:bg-mist transition-colors duration-150"
            aria-label="Cancel edit"
          >
            <X size={14} />
          </button>
        )}
      </div>
      {isEditing && (
        <div className="mt-3 flex flex-col gap-2">
          {error && <p className="text-[13px] text-rose">{error}</p>}
          <div className="flex gap-2">
            <input
              type="number"
              inputMode="decimal"
              step="any"
              min="0"
              value={value}
              onChange={e => setValue(e.target.value)}
              autoFocus
              className="flex-1 h-10 px-3 rounded-lg border border-stone bg-white
                         text-small text-midnight placeholder:text-slate
                         focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                         transition-colors duration-150"
            />
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="h-10 px-4 rounded-lg bg-plum text-white text-[13px] font-semibold
                         flex items-center justify-center gap-1.5
                         hover:bg-rose active:opacity-80 disabled:opacity-50
                         transition-colors duration-150"
            >
              {saving ? 'Saving…' : <><Check size={14} />Save</>}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Crew & Truck Rates section ───────────────────────────────────────────────

interface CrewTruckRatesCardProps {
  rates: ResourceRate[];
  currency: OrgCurrency;
  blendedEnabled: boolean;
  commercialEnabled: boolean;
  onToggleBlended: (enabled: boolean) => Promise<void>;
  onSaveRate: (slug: ResourceSlug, hourlyRate: number) => Promise<void>;
}

function CrewTruckRatesCard({
  rates,
  currency,
  blendedEnabled,
  commercialEnabled,
  onToggleBlended,
  onSaveRate,
}: CrewTruckRatesCardProps) {
  const [editing, setEditing] = useState(false);
  const [editingSlug, setEditingSlug] = useState<ResourceSlug | null>(null);
  const [togglingBlended, setTogglingBlended] = useState(false);

  // Determine which rates to show based on blended mode
  const visibleRates = rates.filter(r => {
    if (blendedEnabled) {
      if (r.slug === 'truck') return false;
      if (r.slug === 'driver') return commercialEnabled;
      return true;
    } else {
      if (r.slug === 'truck_driver') return false;
      return true;
    }
  });

  async function handleToggleBlended(checked: boolean) {
    setTogglingBlended(true);
    try {
      await onToggleBlended(checked);
    } catch {
      // error handled upstream
    }
    setTogglingBlended(false);
  }

  return (
    <div className="bg-white border border-stone rounded-xl shadow-brand-sm overflow-hidden">
      <div className="flex items-center justify-between p-4 md:p-5">
        <span className="font-semibold text-small text-midnight">Crew and Truck Rates</span>
        <button
          type="button"
          onClick={() => { setEditing(!editing); setEditingSlug(null); }}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-semibold
                     text-plum hover:bg-plum/10 transition-colors"
        >
          {editing ? <><X size={12} />Done</> : <><Pencil size={12} />Edit</>}
        </button>
      </div>
      <div className="px-4 pb-4 md:px-5 md:pb-5 border-t border-stone">
        <div className="pt-4">
          <div className="mb-4">
            <ToggleSwitch
              checked={blendedEnabled}
              onChange={handleToggleBlended}
              disabled={togglingBlended || !editing}
              label="Use blended truck + driver rate"
            />
          </div>
          <div>
            {visibleRates.map(rate => (
              <RateLineItem
                key={rate.slug}
                name={rate.name}
                rate={rate.hourlyRate}
                currency={currency}
                editing={editing}
                onEdit={() => setEditingSlug(rate.slug)}
                onSave={async (newRate) => {
                  await onSaveRate(rate.slug, newRate);
                  setEditingSlug(null);
                }}
                onCancel={() => setEditingSlug(null)}
                isEditing={editingSlug === rate.slug}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Residential Flat Rates section ───────────────────────────────────────────

interface ResidentialFlatRatesSectionProps {
  flatRates: ResidentialFlatRates;
  currency: OrgCurrency;
  rangePercent: number | null;
  rates: ResourceRate[];
  useBlended: boolean;
  pricingModel: 'flat_rate' | 'hourly_rate';
  onSave: (data: ResidentialFlatRates) => Promise<void>;
  onSaveRange: (value: number | null) => Promise<void>;
}

// ── Inline editable field for room sub-cards ─────────────────────────────────

function RoomField({
  label,
  value,
  suffix,
  editing,
  onSave,
  step,
  disabled,
  readOnly,
  tooltip,
}: {
  label: string;
  value?: number;
  suffix?: string;
  editing: boolean;
  onSave: (v: number) => Promise<void>;
  step?: string;
  disabled?: boolean;
  readOnly?: boolean;
  tooltip?: React.ReactNode;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [inputValue, setInputValue] = useState(value != null ? String(value) : '');

  useEffect(() => {
    setInputValue(value != null ? String(value) : '');
  }, [value]);

  async function handleSave() {
    const num = parseFloat(inputValue);
    if (isNaN(num) || num < 0) return;
    await onSave(num);
    setIsEditing(false);
  }

  if (isEditing && editing && !disabled && !readOnly) {
    return (
      <div className="flex flex-col gap-1">
        <span className="text-[11px] font-medium text-slate">{label}</span>
        <div className="flex items-center gap-1">
          <input
            type="number"
            inputMode="decimal"
            step={step ?? '1'}
            min="0"
            value={inputValue}
            onChange={e => setInputValue(e.target.value)}
            autoFocus
            onKeyDown={e => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') setIsEditing(false); }}
            className="w-[64px] h-8 px-2 rounded-lg border border-plum bg-white text-[13px] text-midnight text-center
                       focus:outline-none focus:ring-2 focus:ring-plum/20 tabular-nums"
          />
          <button
            type="button"
            onClick={handleSave}
            className="h-8 w-8 flex items-center justify-center rounded-lg bg-plum text-white
                       hover:bg-rose transition-colors shrink-0"
          >
            <Check size={12} />
          </button>
          <button
            type="button"
            onClick={() => setIsEditing(false)}
            className="h-8 w-8 flex items-center justify-center rounded-lg border border-stone text-slate
                       hover:text-midnight transition-colors shrink-0"
          >
            <X size={12} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[11px] font-medium text-slate">{label}</span>
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => { if (editing && !disabled && !readOnly) { setInputValue(value != null ? String(value) : ''); setIsEditing(true); } }}
          disabled={disabled || !editing || readOnly}
          className={`tabular-nums
                     ${editing && !disabled && !readOnly
                       ? 'text-[15px] font-bold text-midnight px-2.5 py-1.5 rounded-lg border border-stone bg-mist/40 hover:border-plum hover:text-plum cursor-pointer transition-colors min-w-[48px] text-center'
                       : `text-[14px] font-bold ${disabled ? 'text-slate' : 'text-midnight'} cursor-default`}`}
        >
          {value != null ? `${value}${suffix ?? ''}` : '—'}
        </button>
        {tooltip}
      </div>
    </div>
  );
}

// ── Room sub-card component ──────────────────────────────────────────────────

function RoomSubCard({
  room,
  currency,
  editing,
  isFlatRate,
  rates,
  useBlended,
  onToggle,
  onSaveField,
}: {
  room: ResidentialFlatRateRoom;
  currency: OrgCurrency;
  editing: boolean;
  isFlatRate: boolean;
  rates: ResourceRate[];
  useBlended: boolean;
  onToggle: (enabled: boolean) => Promise<void>;
  onSaveField: (field: 'flatRate' | 'crewSize' | 'trucks' | 'jobDurationHours', value: number) => Promise<void>;
}) {
  const sym = CURRENCY_SYMBOL[currency];
  const hasCrewData = room.crewSize != null && room.crewSize > 0
    && room.trucks != null && room.trucks > 0
    && room.jobDurationHours != null && room.jobDurationHours > 0;

  const breakdown = useMemo(() => {
    if (!hasCrewData) return null;
    return computeCrewCost(room.crewSize!, room.trucks!, room.jobDurationHours!, rates, useBlended);
  }, [room.crewSize, room.trucks, room.jobDurationHours, rates, useBlended, hasCrewData]);

  const computedPrice = breakdown?.totalCost;
  const isHourly = !isFlatRate;

  // For hourly mode, the displayed price is always the computed value
  const displayPrice = isHourly && computedPrice != null ? computedPrice : room.flatRate;

  function renderPriceTooltip() {
    if (!breakdown || !isHourly) return null;
    return (
      <Tooltip
        content={
          <div>
            <p className="font-semibold text-[12px] text-midnight mb-2">Price Calculation</p>
            <div className="flex flex-col gap-1 text-[11px]">
              {breakdown.supervisorQty > 0 && (
                <div className="flex justify-between">
                  <span className="text-slate">Supervisor ({breakdown.supervisorQty}x)</span>
                  <span className="font-medium tabular-nums">{sym}{(breakdown.supervisorQty * breakdown.supervisorRate).toFixed(2)}/hr</span>
                </div>
              )}
              {breakdown.useBlended ? (
                <>
                  {breakdown.truckDriverQty > 0 && (
                    <div className="flex justify-between">
                      <span className="text-slate">Truck + Driver ({breakdown.truckDriverQty}x)</span>
                      <span className="font-medium tabular-nums">{sym}{(breakdown.truckDriverQty * breakdown.truckDriverRate).toFixed(2)}/hr</span>
                    </div>
                  )}
                  {breakdown.moverQty > 0 && (
                    <div className="flex justify-between">
                      <span className="text-slate">Mover ({breakdown.moverQty}x)</span>
                      <span className="font-medium tabular-nums">{sym}{(breakdown.moverQty * breakdown.moverRate).toFixed(2)}/hr</span>
                    </div>
                  )}
                </>
              ) : (
                <>
                  {breakdown.moverQty > 0 && (
                    <div className="flex justify-between">
                      <span className="text-slate">Mover ({breakdown.moverQty}x)</span>
                      <span className="font-medium tabular-nums">{sym}{(breakdown.moverQty * breakdown.moverRate).toFixed(2)}/hr</span>
                    </div>
                  )}
                  {breakdown.truckQty > 0 && (
                    <div className="flex justify-between">
                      <span className="text-slate">Truck ({breakdown.truckQty}x)</span>
                      <span className="font-medium tabular-nums">{sym}{(breakdown.truckQty * breakdown.truckRate).toFixed(2)}/hr</span>
                    </div>
                  )}
                </>
              )}
              <div className="border-t border-stone/50 my-1" />
              <div className="flex justify-between font-semibold">
                <span className="text-slate">Hourly rate</span>
                <span className="tabular-nums">{sym}{breakdown.totalHourlyRate.toFixed(2)}/hr</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate">Hours</span>
                <span className="tabular-nums">{breakdown.hours}</span>
              </div>
              <div className="border-t border-stone/50 my-1" />
              <div className="flex justify-between font-bold text-[12px]">
                <span>Estimated price</span>
                <span className="tabular-nums">{sym}{breakdown.totalCost.toFixed(2)}</span>
              </div>
            </div>
          </div>
        }
      >
        <Info size={14} className="text-slate hover:text-plum transition-colors cursor-pointer" />
      </Tooltip>
    );
  }

  return (
    <div className={`rounded-xl border transition-colors duration-150
                    ${room.enabled ? 'border-stone bg-white' : 'border-stone/60 bg-mist/50'}`}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3">
        <div className="flex items-center gap-3 min-w-0">
          {editing && (
            <input
              type="checkbox"
              checked={room.enabled}
              onChange={e => onToggle(e.target.checked)}
              className="h-4 w-4 rounded border-stone text-plum focus:ring-plum/20 shrink-0"
            />
          )}
          <span className={`text-[14px] font-semibold ${room.enabled ? 'text-midnight' : 'text-slate'}`}>
            {room.label}
          </span>
        </div>
        {room.enabled && (
          <div className="flex items-center gap-1.5 shrink-0">
            {isHourly && computedPrice != null && renderPriceTooltip()}
            <span className={`text-[16px] font-bold tabular-nums ${room.enabled ? 'text-midnight' : 'text-slate'}`}>
              {sym}{displayPrice.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
            </span>
          </div>
        )}
        {!room.enabled && !editing && (
          <span className="text-[12px] text-slate italic">Not available</span>
        )}
      </div>

      {/* Body — crew / trucks / hours fields */}
      {room.enabled && (
        <div className="px-4 pb-3.5 pt-0.5">
          <div className="flex items-start gap-5 sm:gap-8">
            <RoomField
              label="Crew"
              value={room.crewSize}
              editing={editing}
              onSave={async (v) => onSaveField('crewSize', v)}
            />
            <RoomField
              label="Trucks"
              value={room.trucks}
              editing={editing}
              onSave={async (v) => onSaveField('trucks', v)}
            />
            <RoomField
              label="Hours"
              value={room.jobDurationHours}
              suffix=" hrs"
              editing={editing}
              onSave={async (v) => onSaveField('jobDurationHours', v)}
              step="0.5"
            />
            {isFlatRate && (
              <RoomField
                label="Price"
                value={room.flatRate}
                editing={editing}
                onSave={async (v) => onSaveField('flatRate', v)}
              />
            )}
          </div>
          {isHourly && !hasCrewData && (
            <p className="text-[11px] text-ember mt-2">
              Set crew size, trucks, and hours to calculate an estimated price.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ── Residential Flat Rates section (main) ────────────────────────────────────

function ResidentialFlatRatesSection({
  flatRates,
  currency,
  rangePercent,
  rates,
  useBlended,
  pricingModel,
  onSave,
  onSaveRange,
}: ResidentialFlatRatesSectionProps) {
  const [editing, setEditing] = useState(false);
  const [editingItem, setEditingItem] = useState<string | null>(null);
  const [editingRange, setEditingRange] = useState(false);
  const [rangeValue, setRangeValue] = useState(rangePercent != null ? String(rangePercent) : '');
  const [rangeSaving, setRangeSaving] = useState(false);
  const [rangeError, setRangeError] = useState('');

  const isFlatRate = pricingModel === 'flat_rate';

  useEffect(() => {
    setRangeValue(rangePercent != null ? String(rangePercent) : '');
  }, [rangePercent]);

  async function handleToggleRoom(bedroomCount: number, enabled: boolean) {
    const updated: ResidentialFlatRates = {
      ...flatRates,
      rooms: flatRates.rooms.map(r =>
        r.bedroomCount === bedroomCount
          ? { ...r, enabled, flatRate: enabled ? r.flatRate : 0 }
          : r,
      ),
    };
    await onSave(updated);
  }

  async function handleSaveRoomField(
    bedroomCount: number,
    field: 'flatRate' | 'crewSize' | 'trucks' | 'jobDurationHours',
    value: number,
  ) {
    const updated: ResidentialFlatRates = {
      ...flatRates,
      rooms: flatRates.rooms.map(r =>
        r.bedroomCount === bedroomCount ? { ...r, [field]: value } : r,
      ),
    };
    await onSave(updated);
  }

  async function handleSaveAddon(slug: string, newRate: number) {
    const updated: ResidentialFlatRates = {
      ...flatRates,
      addons: flatRates.addons.map(a =>
        a.slug === slug ? { ...a, flatRate: newRate } : a,
      ),
    };
    await onSave(updated);
    setEditingItem(null);
  }

  async function handleSaveRange() {
    const trimmed = rangeValue.trim();
    if (trimmed === '') {
      setRangeSaving(true);
      setRangeError('');
      try {
        await onSaveRange(null);
        setEditingRange(false);
      } catch (err) {
        setRangeError(err instanceof Error ? err.message : 'Failed to save.');
      }
      setRangeSaving(false);
      return;
    }
    const num = parseFloat(trimmed);
    if (isNaN(num) || num < 0 || num > 100) {
      setRangeError('Enter a percentage between 0 and 100, or leave blank.');
      return;
    }
    setRangeSaving(true);
    setRangeError('');
    try {
      await onSaveRange(num);
      setEditingRange(false);
    } catch (err) {
      setRangeError(err instanceof Error ? err.message : 'Failed to save.');
    }
    setRangeSaving(false);
  }

  return (
    <div className="pt-4">
      <div className="flex items-center justify-end mb-3">
        <button
          type="button"
          onClick={() => { setEditing(!editing); setEditingItem(null); }}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-semibold
                     text-plum hover:bg-plum/10 transition-colors"
        >
          {editing ? <><X size={12} />Done</> : <><Pencil size={12} />Edit</>}
        </button>
      </div>

      {/* Mode callout */}
      {isFlatRate ? (
        <div className="mb-4 px-3.5 py-3 rounded-lg bg-plum/5 border border-plum/20">
          <p className="text-[12px] text-midnight leading-snug">
            <span className="font-semibold">Flat rate mode.</span>{' '}
            Filling out crew size, hours, and trucks for each room count is recommended for improved scheduling and job assignment.
          </p>
        </div>
      ) : (
        <div className="mb-4 px-3.5 py-3 rounded-lg bg-plum/5 border border-plum/20">
          <p className="text-[12px] text-midnight leading-snug">
            <span className="font-semibold">Hourly rate mode.</span>{' '}
            The estimate for new leads will be based on the hourly rate for the crew and truck quantities provided. Prices are auto-calculated from your rate card.
          </p>
        </div>
      )}

      {/* Room Count section */}
      <p className="text-[11px] font-bold uppercase tracking-widest text-slate mb-3">
        Room Count
      </p>
      <div className="flex flex-col gap-2.5">
        {flatRates.rooms.map(room => (
          <RoomSubCard
            key={`room-${room.bedroomCount}`}
            room={room}
            currency={currency}
            editing={editing}
            isFlatRate={isFlatRate}
            rates={rates}
            useBlended={useBlended}
            onToggle={async (enabled) => handleToggleRoom(room.bedroomCount, enabled)}
            onSaveField={async (field, value) => handleSaveRoomField(room.bedroomCount, field, value)}
          />
        ))}
      </div>

      {/* Add-ons section */}
      <p className="text-[11px] font-bold uppercase tracking-widest text-slate mt-5 mb-2">
        Add-ons
      </p>
      <div>
        {flatRates.addons.map(addon => (
          <RateLineItem
            key={`addon-${addon.slug}`}
            name={addon.label}
            rate={addon.flatRate}
            currency={currency}
            formatFn={formatFlatRate}
            editing={editing}
            onEdit={() => setEditingItem(`addon-${addon.slug}`)}
            onSave={async (newRate) => handleSaveAddon(addon.slug, newRate)}
            onCancel={() => setEditingItem(null)}
            isEditing={editingItem === `addon-${addon.slug}`}
          />
        ))}
      </div>

      {/* Specialty Items toggle */}
      <div className="mt-5 pt-4 border-t border-stone">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={flatRates.specialtyItemsEnabled ?? false}
            onChange={async (e) => {
              const updated: ResidentialFlatRates = { ...flatRates, specialtyItemsEnabled: e.target.checked };
              await onSave(updated);
            }}
            className="h-5 w-5 mt-0.5 rounded border-stone text-plum focus:ring-plum/20 shrink-0"
          />
          <div className="flex-1 min-w-0">
            <p className="text-small text-midnight font-medium">Specialty Items</p>
            <p className="text-[12px] text-slate mt-0.5">
              When enabled, a specialty items section appears in the customer-facing booking form.
              If the customer checks it, they will not receive an instant estimate and will instead be prompted to request a free estimate.
            </p>
          </div>
        </label>
      </div>

      {/* Estimate Range Percentage */}
      <div className="mt-5 pt-4 border-t border-stone">
        <div className="flex items-center gap-3">
          <div className="flex-1 min-w-0">
            <p className="text-small text-midnight">Estimate Range</p>
            <p className="text-[12px] text-slate mt-0.5">
              Optional. Shows a price range (e.g. ±20%) instead of a single estimate on leads and booking forms.
            </p>
          </div>
          {!editingRange && (
            <>
              <span className="font-mono text-small font-bold text-midnight tabular-nums">
                {rangePercent != null ? `${rangePercent}%` : '—'}
              </span>
              {editing && (
                <button
                  type="button"
                  onClick={() => {
                    setRangeValue(rangePercent != null ? String(rangePercent) : '');
                    setRangeError('');
                    setEditingRange(true);
                  }}
                  className="shrink-0 h-8 w-8 flex items-center justify-center rounded-lg border border-stone
                             text-slate hover:border-plum hover:text-plum active:bg-mist
                             transition-colors duration-150"
                  aria-label="Edit range"
                >
                  <Pencil size={14} />
                </button>
              )}
            </>
          )}
          {editingRange && (
            <button
              type="button"
              onClick={() => { setRangeError(''); setEditingRange(false); }}
              className="shrink-0 h-8 w-8 flex items-center justify-center rounded-lg border border-stone
                         text-slate hover:text-midnight active:bg-mist transition-colors duration-150"
              aria-label="Cancel edit"
            >
              <X size={14} />
            </button>
          )}
        </div>
        {editingRange && (
          <div className="mt-3 flex flex-col gap-2">
            {rangeError && <p className="text-[13px] text-rose">{rangeError}</p>}
            <div className="flex gap-2">
              <div className="relative flex-1">
                <input
                  type="number"
                  inputMode="decimal"
                  step="any"
                  min="0"
                  max="100"
                  value={rangeValue}
                  onChange={e => setRangeValue(e.target.value)}
                  placeholder="Leave blank to disable"
                  autoFocus
                  className="w-full h-10 px-3 pr-8 rounded-lg border border-stone bg-white
                             text-small text-midnight placeholder:text-slate
                             focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                             transition-colors duration-150"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-small text-slate">%</span>
              </div>
              <button
                type="button"
                onClick={handleSaveRange}
                disabled={rangeSaving}
                className="h-10 px-4 rounded-lg bg-plum text-white text-[13px] font-semibold
                           flex items-center justify-center gap-1.5
                           hover:bg-rose active:opacity-80 disabled:opacity-50
                           transition-colors duration-150"
              >
                {rangeSaving ? 'Saving…' : <><Check size={14} />Save</>}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Commercial Ballpark Estimates section ─────────────────────────────────────

const BALLPARK_METHOD_OPTIONS: { value: CommercialBallparkMethod; label: string }[] = [
  { value: 'per_employee', label: 'Per Employee / Workstation' },
  { value: 'per_sqft', label: 'Per Square Foot' },
];

function rateLabel(method: CommercialBallparkMethod): string {
  return method === 'per_employee' ? 'Rate per Employee / Workstation' : 'Rate per Square Foot';
}

interface CommercialBallparkSectionProps {
  method: CommercialBallparkMethod;
  rate: number;
  rangePercent: number | null;
  currency: OrgCurrency;
  onSave: (updates: {
    commercialBallparkMethod?: CommercialBallparkMethod;
    commercialBallparkRate?: number;
    commercialBallparkRangePercent?: number | null;
  }) => Promise<void>;
}

function CommercialBallparkSection({
  method,
  rate,
  rangePercent,
  currency,
  onSave,
}: CommercialBallparkSectionProps) {
  const [editing, setEditing] = useState(false);
  const [editingField, setEditingField] = useState<'rate' | 'range' | null>(null);
  const [rateValue, setRateValue] = useState(String(rate));
  const [rangeValue, setRangeValue] = useState(rangePercent != null ? String(rangePercent) : '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setRateValue(String(rate));
    setRangeValue(rangePercent != null ? String(rangePercent) : '');
  }, [rate, rangePercent]);

  async function handleMethodChange(newMethod: CommercialBallparkMethod) {
    setSaving(true);
    try {
      await onSave({ commercialBallparkMethod: newMethod });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save.');
    }
    setSaving(false);
  }

  async function handleSaveRate() {
    const num = parseFloat(rateValue);
    if (isNaN(num) || num < 0) {
      setError('Enter a valid rate (0 or greater).');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await onSave({ commercialBallparkRate: num });
      setEditingField(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save.');
    }
    setSaving(false);
  }

  async function handleSaveRange() {
    const trimmed = rangeValue.trim();
    if (trimmed === '') {
      setSaving(true);
      setError('');
      try {
        await onSave({ commercialBallparkRangePercent: null });
        setEditingField(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to save.');
      }
      setSaving(false);
      return;
    }
    const num = parseFloat(trimmed);
    if (isNaN(num) || num < 0 || num > 100) {
      setError('Enter a percentage between 0 and 100, or leave blank.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await onSave({ commercialBallparkRangePercent: num });
      setEditingField(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save.');
    }
    setSaving(false);
  }

  return (
    <div className="pt-4">
      <div className="flex items-center justify-end mb-3">
        <button
          type="button"
          onClick={() => { setEditing(!editing); setEditingField(null); }}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-semibold
                     text-plum hover:bg-plum/10 transition-colors"
        >
          {editing ? <><X size={12} />Done</> : <><Pencil size={12} />Edit</>}
        </button>
      </div>
      {/* Method selector */}
      <p className="text-[11px] font-bold uppercase tracking-widest text-slate mb-2">
        Ballpark Method
      </p>
      <div className="flex gap-2 mb-4">
        {BALLPARK_METHOD_OPTIONS.map(opt => (
          <button
            key={opt.value}
            type="button"
            disabled={!editing || saving}
            onClick={() => handleMethodChange(opt.value)}
            className={`flex-1 h-10 px-3 rounded-lg text-[13px] font-medium border
                       transition-colors duration-150
                       ${method === opt.value
                         ? 'border-plum bg-plum/5 text-plum'
                         : 'border-stone bg-white text-slate'}
                       ${editing && !saving ? 'hover:border-plum/40 cursor-pointer' : 'cursor-default'}
                       disabled:opacity-60`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {/* Rate */}
      <div className="py-3 border-b border-stone">
        <div className="flex items-center gap-3">
          <div className="flex-1 min-w-0">
            <p className="text-small text-midnight">{rateLabel(method)}</p>
          </div>
          {editingField !== 'rate' && (
            <>
              <span className="font-mono text-small font-bold text-midnight tabular-nums">
                {CURRENCY_SYMBOL[currency]}{rate}
              </span>
              {editing && (
                <button
                  type="button"
                  onClick={() => { setRateValue(String(rate)); setError(''); setEditingField('rate'); }}
                  className="shrink-0 h-8 w-8 flex items-center justify-center rounded-lg border border-stone
                             text-slate hover:border-plum hover:text-plum active:bg-mist
                             transition-colors duration-150"
                  aria-label="Edit rate"
                >
                  <Pencil size={14} />
                </button>
              )}
            </>
          )}
          {editingField === 'rate' && (
            <button
              type="button"
              onClick={() => { setError(''); setEditingField(null); }}
              className="shrink-0 h-8 w-8 flex items-center justify-center rounded-lg border border-stone
                         text-slate hover:text-midnight active:bg-mist transition-colors duration-150"
              aria-label="Cancel edit"
            >
              <X size={14} />
            </button>
          )}
        </div>
        {editingField === 'rate' && (
          <div className="mt-3 flex flex-col gap-2">
            {error && <p className="text-[13px] text-rose">{error}</p>}
            <div className="flex gap-2">
              <input
                type="number"
                inputMode="decimal"
                step="any"
                min="0"
                value={rateValue}
                onChange={e => setRateValue(e.target.value)}
                autoFocus
                className="flex-1 h-10 px-3 rounded-lg border border-stone bg-white
                           text-small text-midnight placeholder:text-slate
                           focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                           transition-colors duration-150"
              />
              <button
                type="button"
                onClick={handleSaveRate}
                disabled={saving}
                className="h-10 px-4 rounded-lg bg-plum text-white text-[13px] font-semibold
                           flex items-center justify-center gap-1.5
                           hover:bg-rose active:opacity-80 disabled:opacity-50
                           transition-colors duration-150"
              >
                {saving ? 'Saving…' : <><Check size={14} />Save</>}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Estimate Range Percentage */}
      <div className="py-3">
        <div className="flex items-center gap-3">
          <div className="flex-1 min-w-0">
            <p className="text-small text-midnight">Estimate Range</p>
            <p className="text-[12px] text-slate mt-0.5">
              Optional. Shows a price range (e.g. +/- 20%) on ballpark estimates.
            </p>
          </div>
          {editingField !== 'range' && (
            <>
              <span className="font-mono text-small font-bold text-midnight tabular-nums">
                {rangePercent != null ? `${rangePercent}%` : '—'}
              </span>
              {editing && (
                <button
                  type="button"
                  onClick={() => {
                    setRangeValue(rangePercent != null ? String(rangePercent) : '');
                    setError('');
                    setEditingField('range');
                  }}
                  className="shrink-0 h-8 w-8 flex items-center justify-center rounded-lg border border-stone
                             text-slate hover:border-plum hover:text-plum active:bg-mist
                             transition-colors duration-150"
                  aria-label="Edit range"
                >
                  <Pencil size={14} />
                </button>
              )}
            </>
          )}
          {editingField === 'range' && (
            <button
              type="button"
              onClick={() => { setError(''); setEditingField(null); }}
              className="shrink-0 h-8 w-8 flex items-center justify-center rounded-lg border border-stone
                         text-slate hover:text-midnight active:bg-mist transition-colors duration-150"
              aria-label="Cancel edit"
            >
              <X size={14} />
            </button>
          )}
        </div>
        {editingField === 'range' && (
          <div className="mt-3 flex flex-col gap-2">
            {error && <p className="text-[13px] text-rose">{error}</p>}
            <div className="flex gap-2">
              <div className="relative flex-1">
                <input
                  type="number"
                  inputMode="decimal"
                  step="any"
                  min="0"
                  max="100"
                  value={rangeValue}
                  onChange={e => setRangeValue(e.target.value)}
                  placeholder="Leave blank to disable"
                  autoFocus
                  className="w-full h-10 px-3 pr-8 rounded-lg border border-stone bg-white
                             text-small text-midnight placeholder:text-slate
                             focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                             transition-colors duration-150"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-small text-slate">%</span>
              </div>
              <button
                type="button"
                onClick={handleSaveRange}
                disabled={saving}
                className="h-10 px-4 rounded-lg bg-plum text-white text-[13px] font-semibold
                           flex items-center justify-center gap-1.5
                           hover:bg-rose active:opacity-80 disabled:opacity-50
                           transition-colors duration-150"
              >
                {saving ? 'Saving…' : <><Check size={14} />Save</>}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Inner page (requires orgId) ──────────────────────────────────────────────

function RateCardsContent({ orgId }: { orgId: string }) {
  const navigate = useNavigate();
  const { org, updateSettings } = useOrgSettings(orgId);
  const { rates, loading: ratesLoading, error: ratesError, updateRate } = useOrgResourceRates(orgId);

  const commercialEnabled = org?.featureCommercialMovingQuotes ?? true;
  const residentialEnabled = org?.featureResidentialMovingQuotes ?? false;
  const blendedEnabled = org?.useBlendedTruckDriverRate ?? false;
  const flatRatesFeatureEnabled = org?.residentialFlatRatesEnabled ?? false;
  const ballparkEnabled = org?.commercialBallparkEnabled ?? false;
  const ballparkMethod: CommercialBallparkMethod = org?.commercialBallparkMethod ?? 'per_employee';
  const ballparkRate = org?.commercialBallparkRate ?? 0;
  const ballparkRangePercent = org?.commercialBallparkRangePercent ?? null;
  const residentialRangePercent = org?.residentialBallparkRangePercent ?? null;
  const residentialPricingModel: 'flat_rate' | 'hourly_rate' = org?.residentialPricingModel ?? 'hourly_rate';

  const {
    flatRates,
    loading: flatRatesLoading,
    error: flatRatesError,
    updateFlatRates,
  } = useResidentialFlatRates(orgId);

  const currency = org?.defaultCurrency ?? 'USD';
  const loading = ratesLoading || flatRatesLoading;
  const error = ratesError || flatRatesError;

  const [togglingFlatRates, setTogglingFlatRates] = useState(false);
  const [togglingBallpark, setTogglingBallpark] = useState(false);

  async function handleToggleBlended(enabled: boolean) {
    await updateSettings({ useBlendedTruckDriverRate: enabled });
  }

  async function handleToggleFlatRatesEnabled(enabled: boolean) {
    setTogglingFlatRates(true);
    try {
      await updateSettings({ residentialFlatRatesEnabled: enabled });
    } catch {
      // error handled by hook
    }
    setTogglingFlatRates(false);
  }

  async function handleSaveRate(slug: ResourceSlug, hourlyRate: number) {
    await updateRate(slug, hourlyRate);
  }

  async function handleSaveFlatRates(data: ResidentialFlatRates) {
    await updateFlatRates(data);
  }

  async function handleToggleBallparkEnabled(enabled: boolean) {
    setTogglingBallpark(true);
    try {
      await updateSettings({ commercialBallparkEnabled: enabled });
    } catch {
      // error handled by hook
    }
    setTogglingBallpark(false);
  }

  async function handleSaveResidentialRange(value: number | null) {
    await updateSettings({ residentialBallparkRangePercent: value });
  }

  async function handleSaveBallpark(updates: {
    commercialBallparkMethod?: CommercialBallparkMethod;
    commercialBallparkRate?: number;
    commercialBallparkRangePercent?: number | null;
  }) {
    await updateSettings(updates);
  }

  return (
    <div className="px-4 pt-6 pb-8 md:px-8 md:max-w-4xl lg:max-w-6xl md:mx-auto">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-1 text-small mb-4 -ml-1">
        <button
          type="button"
          onClick={() => navigate('/admin')}
          className="text-slate hover:text-midnight transition-colors duration-150"
        >
          Admin Panel
        </button>
        <ChevronRight size={14} className="text-stone" />
        <button
          type="button"
          onClick={() => navigate('/admin/quoting')}
          className="text-slate hover:text-midnight transition-colors duration-150"
        >
          Quoting
        </button>
        <ChevronRight size={14} className="text-stone" />
        <span className="text-midnight font-medium">Rate Cards</span>
      </nav>

      <h1 className="font-display text-h1 text-midnight mb-1">Rate Cards</h1>
      <p className="text-[13px] text-slate mb-6 leading-snug">
        Configure default rates for crew, equipment, and services.
        These defaults can be overridden for individual quotes.
      </p>

      {loading && (
        <div className="flex justify-center py-16">
          <LoadingSpinner size="lg" />
        </div>
      )}

      {!loading && error && (
        <div className="flex flex-col items-center py-16 gap-2 text-center">
          <p className="text-small text-rose font-semibold">Failed to load rate cards</p>
          <p className="text-small text-slate">{error}</p>
        </div>
      )}

      {!loading && !error && (
        <div className="flex flex-col gap-4 lg:grid lg:grid-cols-2 lg:gap-5 lg:items-start">
          {/* Left column: Crew & Truck Rates + Commercial Ballpark */}
          <div className="flex flex-col gap-4">
            <CrewTruckRatesCard
              rates={rates}
              currency={currency}
              blendedEnabled={blendedEnabled}
              commercialEnabled={commercialEnabled}
              onToggleBlended={handleToggleBlended}
              onSaveRate={handleSaveRate}
            />

            {/* Commercial Ballpark Estimates — only if org has commercial feature */}
            {commercialEnabled && (
              <CollapsibleCard
                title="Commercial Ballpark Estimates"
                defaultExpanded={ballparkEnabled}
                expandable={ballparkEnabled}
                disabled={!ballparkEnabled}
                headerRight={
                  <ToggleSwitch
                    checked={ballparkEnabled}
                    onChange={handleToggleBallparkEnabled}
                    disabled={togglingBallpark}
                    label=""
                  />
                }
              >
                <CommercialBallparkSection
                  method={ballparkMethod}
                  rate={ballparkRate}
                  rangePercent={ballparkRangePercent}
                  currency={currency}
                  onSave={handleSaveBallpark}
                />
              </CollapsibleCard>
            )}
          </div>

          {/* Right column: Residential Flat Rates */}
          {residentialEnabled && (
            <CollapsibleCard
              title="Residential Flat Rates"
              defaultExpanded={flatRatesFeatureEnabled}
              expandable={flatRatesFeatureEnabled}
              disabled={!flatRatesFeatureEnabled}
              headerRight={
                <ToggleSwitch
                  checked={flatRatesFeatureEnabled}
                  onChange={handleToggleFlatRatesEnabled}
                  disabled={togglingFlatRates}
                  label=""
                />
              }
            >
              {flatRates && (
                <ResidentialFlatRatesSection
                  flatRates={flatRates}
                  currency={currency}
                  rangePercent={residentialRangePercent}
                  rates={rates}
                  useBlended={blendedEnabled}
                  pricingModel={residentialPricingModel}
                  onSave={handleSaveFlatRates}
                  onSaveRange={handleSaveResidentialRange}
                />
              )}
            </CollapsibleCard>
          )}
        </div>
      )}
    </div>
  );
}

// ── Page (guards org admin access) ──────────────────────────────────────────

export function OrgResourceRatesPage() {
  const { adminOrg, loading } = useUserProfile();

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!adminOrg) {
    return (
      <div className="px-4 pt-6 flex flex-col items-center py-20 gap-3 text-center">
        <p className="text-small text-slate">You don't have org admin access.</p>
      </div>
    );
  }

  return <RateCardsContent orgId={adminOrg.orgId} />;
}
